import { useState } from "react";
import type { HeatCell, InsightDocument, MilestoneStatus } from "../../api/insights";
import { formatScore, moduleLabel, moduleShortLabel, riskLevelColor, riskLevelLabel, riskLevelTint } from "../../lib/riskVocabulary";
import { NAVY, shortDate } from "./primitives";

/** Visual building blocks of the cockpit: regulatory clock and business-unit heatmap. */

const SCOPE_LABEL: Record<MilestoneStatus["scope"], string> = {
  plan: "Plan / inventory",
  begin: "Begin migration",
  high_priority: "High-priority migrated",
  all: "All migrated",
  deprecate: "Deprecated",
  disallow: "Disallowed",
  procurement: "PQC in procurement",
};

export const scopeLabel = (s: MilestoneStatus["scope"]) => SCOPE_LABEL[s] ?? s;

const COMPLETION = new Set(["high_priority", "all", "deprecate", "disallow"]);

export const APPLICABILITY_TAG: Record<string, string> = { obligation: "Binding", guidance: "Guidance", reference: "Benchmark", other_sector: "Other sectors" };

/** Status tag as the reports print it: Binding / Expected (supervisory expectation) / Guidance / Benchmark. */
export function statusTag(applicability: string, binding: string | null): string {
  if (applicability === "obligation") return binding === "expected" ? "Expected" : "Binding";
  return APPLICABILITY_TAG[applicability] ?? applicability;
}

function milestoneColor(m: MilestoneStatus): string {
  if (m.days_remaining < 0) return "#64748b";
  if (m.applicability === "reference") return "#8a94a6";
  if (m.assets_projected_to_miss > 0) return riskLevelColor("Critical");
  if (m.days_remaining <= 548) return riskLevelColor("High");
  return NAVY;
}

/**
 * Regulatory clock: one lane per jurisdiction, milestones on a shared time
 * axis, today and the planning year marked. A red marker means assets
 * in scope are projected to miss that deadline if migration started today.
 */
export function RegulatoryClock({ milestones, crqcYear, asOf }: { milestones: MilestoneStatus[]; crqcYear: number | null; asOf: string | null }) {
  const [hover, setHover] = useState<MilestoneStatus | null>(null);
  if (milestones.length === 0) return <p className="text-xs text-ink-500 py-6 text-center">No regulatory milestones for the selected jurisdictions.</p>;
  const today = asOf ? new Date(asOf) : new Date();
  const y0 = today.getUTCFullYear();
  const lastYear = Math.max(...milestones.map((m) => Number(m.date.slice(0, 4))), crqcYear ?? 0, y0 + 9) + 1;
  const lanes = [...new Set(milestones.map((m) => m.jurisdiction))];
  const W = 720, labelW = 48, R = 12, laneH = 30, T = 22, H = T + lanes.length * laneH + 22;
  const t0 = Date.UTC(y0, 0, 1), t1 = Date.UTC(lastYear, 0, 1);
  const x = (ms: number) => labelW + ((ms - t0) / (t1 - t0)) * (W - labelW - R);
  const xToday = x(today.getTime());
  const years = Array.from({ length: lastYear - y0 + 1 }, (_, i) => y0 + i);
  return (
    <div className="relative" data-testid="regulatory-clock">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Regulatory milestones by jurisdiction">
        {years.map((yy) => (
          <g key={yy}>
            <line x1={x(Date.UTC(yy, 0, 1))} x2={x(Date.UTC(yy, 0, 1))} y1={T - 4} y2={H - 20} stroke="#eeeeea" />
            <text x={x(Date.UTC(yy, 0, 1))} y={H - 6} fontSize="10" fill="#5c5c57" textAnchor="middle">{yy}</text>
          </g>
        ))}
        {lanes.map((j, i) => (
          <g key={j}>
            <text x={8} y={T + i * laneH + laneH / 2 + 4} fontSize="11" fontWeight="600" fill="#2e2e2c">{j}</text>
            <line x1={labelW} x2={W - R} y1={T + i * laneH + laneH / 2} y2={T + i * laneH + laneH / 2} stroke="#d6d6d2" />
          </g>
        ))}
        <rect x={labelW} y={T - 4} width={Math.max(0, xToday - labelW)} height={H - T - 16} fill="#f5f5f2" opacity={0.7} />
        <line x1={xToday} x2={xToday} y1={T - 10} y2={H - 20} stroke={NAVY} strokeWidth={1.5} />
        <text x={xToday} y={T - 12} fontSize="10" fill={NAVY} textAnchor="middle" fontWeight="600">Today</text>
        {crqcYear && crqcYear < lastYear && (
          <g>
            <line x1={x(Date.UTC(crqcYear, 0, 1))} x2={x(Date.UTC(crqcYear, 0, 1))} y1={T - 10} y2={H - 20} stroke={riskLevelColor("Critical")} strokeWidth={1.5} strokeDasharray="4 3" />
            <text x={x(Date.UTC(crqcYear, 0, 1))} y={T - 12} fontSize="10" fill={riskLevelColor("Critical")} textAnchor="middle" fontWeight="600">CRQC planning year {crqcYear}</text>
          </g>
        )}
        {milestones.map((m) => {
          const lane = lanes.indexOf(m.jurisdiction);
          const cx = x(Date.parse(`${m.date}T00:00:00Z`));
          const cy = T + lane * laneH + laneH / 2;
          const c = milestoneColor(m);
          const completion = COMPLETION.has(m.scope);
          return (
            <g key={m.id} onMouseEnter={() => setHover(m)} onMouseLeave={() => setHover(null)} style={{ cursor: "default" }} data-testid={`milestone-${m.id}`}>
              {completion
                ? <rect x={cx - 5.5} y={cy - 5.5} width={11} height={11} transform={`rotate(45 ${cx} ${cy})`} fill={m.applicability === "obligation" ? c : "#fff"} stroke={m.applicability === "obligation" ? "#fff" : c} strokeWidth={m.applicability === "obligation" ? 1.5 : 2} />
                : <circle cx={cx} cy={cy} r={5} fill="#fff" stroke={c} strokeWidth={2} />}
              {m.assets_projected_to_miss > 0 && (
                <text x={cx} y={cy - 9} fontSize="9.5" fontWeight="700" fill={c} textAnchor="middle">{m.assets_projected_to_miss}</text>
              )}
              <title>{`${m.short_title ?? m.framework} (${statusTag(m.applicability, m.binding)}) · ${scopeLabel(m.scope)} by ${m.date}`}</title>
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-500 mt-1">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rotate-45" style={{ background: NAVY }} />Completion date, binding</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rotate-45 border-2" style={{ borderColor: NAVY }} />Completion date, guidance or benchmark</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border-2" style={{ borderColor: NAVY }} />Plan / begin / procure</span>
        {milestones.some((m) => m.assets_projected_to_miss > 0) && (
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rotate-45" style={{ background: riskLevelColor("Critical") }} />Assets projected to miss (number shown)</span>
        )}
        {milestones.some((m) => m.assets_projected_to_miss === 0 && m.days_remaining >= 0 && m.days_remaining <= 548) && (
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: riskLevelColor("High") }} />Within 18 months</span>
        )}
      </div>
      {milestones.some((m) => m.assets_projected_to_miss > 0) && (
        <ul className="mt-2 text-[11px] text-ink-700 space-y-0.5" data-testid="clock-counts">
          {milestones.filter((m) => m.assets_projected_to_miss > 0).map((m) => (
            <li key={m.id}><strong style={{ color: milestoneColor(m) }}>{m.assets_projected_to_miss}</strong> {m.assets_projected_to_miss === 1 ? "asset" : "assets"} projected to miss {m.short_title ?? m.framework} ({scopeLabel(m.scope).toLowerCase()} by {shortDate(m.date)})</li>
          ))}
        </ul>
      )}
      {hover && (
        <div className="absolute top-0 right-0 max-w-[320px] bg-white border border-ink-200 shadow-sm rounded-sm px-3 py-2 text-[11px] text-ink-700 pointer-events-none">
          <div className="font-semibold text-ink-900">{hover.short_title ?? hover.framework}</div>
          <div className="text-ink-500">{hover.issuer ? `${hover.issuer} · ` : ""}{statusTag(hover.applicability, hover.binding)} · {hover.applies_to}</div>
          <div>{scopeLabel(hover.scope)}{hover.category ? ` · ${hover.category}` : ""} — {shortDate(hover.date)}</div>
          <div className="text-ink-500 mt-0.5">{hover.description}</div>
          {COMPLETION.has(hover.scope) && (
            <div className="mt-1">{hover.assets_in_scope} in scope · <strong style={{ color: milestoneColor(hover) }}>{hover.assets_projected_to_miss} projected to miss</strong>{hover.assets_unknown > 0 ? ` · ${hover.assets_unknown} without estimate` : ""}</div>
          )}
          <div className="text-ink-400 mt-0.5">{hover.instrument_status}{hover.verification === "secondary" ? " · secondary source" : ""}</div>
        </div>
      )}
    </div>
  );
}

/** BU × dimension heatmap. Cells show the band and the High+ count, not decimals; scores are in the tooltip. */
export function Heatmap({ doc }: { doc: InsightDocument }) {
  const cell = (bu: string, m: string): HeatCell | undefined => doc.heatmap.cells.find((c) => c.business_unit_id === bu && c.module_code === m);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="heatmap">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-ink-500">
            <th className="text-left px-2 py-1.5">Business unit</th>
            {doc.heatmap.modules.map((m) => (
              <th key={m} className={`px-1 py-1.5 text-center ${m.startsWith("integrated") ? "font-semibold text-ink-700" : ""}`}>{moduleShortLabel(m)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {doc.heatmap.business_units.map((b) => (
            <tr key={b.id} className="border-t border-ink-50">
              <td className="px-2 py-1 font-medium text-ink-800 whitespace-nowrap">{b.name} <span className="text-ink-400 font-normal">({b.assets})</span></td>
              {doc.heatmap.modules.map((m) => {
                const c = cell(b.id, m);
                const bg = c?.band ? riskLevelTint(c.band) : "#f8fafc";
                const fg = c?.band ? riskLevelColor(c.band) : "#94a3b8";
                return (
                  <td key={m} className="px-0.5 py-0.5">
                    <div className="rounded-sm text-center py-1 font-semibold leading-tight" style={{ background: bg, color: fg, border: `1px solid ${fg}22` }}
                      title={c ? `${moduleLabel(m)} · ${b.name}: mean ${formatScore(c.mean)}, ${c.high_plus} of ${c.scored} High+` : "Not scored"}>
                      {c?.band ? riskLevelLabel(c.band) : "—"}
                      {c && c.high_plus > 0 && <span className="block text-[9px] font-normal opacity-80">{c.high_plus}/{c.scored} High+</span>}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
